/**
 * LinguistPro — Log a Support Ticket
 * SPEC-004 · Route: /support/new (auth required)
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { helpUrl } from '../lib/legalUrls';
import { useAuthStore } from '../store/useAuthStore';

const CATEGORIES = [
  { value: 'bug',     label: 'Bug / Technical issue' },
  { value: 'billing', label: 'Billing or payment' },
  { value: 'feature', label: 'Feature request' },
  { value: 'other',   label: 'Other' },
] as const;

export default function SupportNewPage() {
  const { session } = useAuthStore();
  const navigate = useNavigate();

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('bug');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<string | null>(null); // ticket ref

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch('/api/support/create-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ subject: subject.trim(), category, description: description.trim() }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to submit ticket');
      setSubmitted(data.ticket_ref);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-[#00d4b8]/10 border border-[#00d4b8]/25 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-[#007a6d]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Ticket submitted</h1>
          <p className="text-gray-500 mb-2">Your reference number is:</p>
          <p className="text-3xl font-mono font-bold text-[#007a6d] mb-6">{submitted}</p>
          <p className="text-gray-500 text-sm mb-8">
            We'll review your ticket and respond within 1 business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/support/my-tickets"
              className="px-5 py-2.5 rounded-xl bg-[#00d4b8] text-[#0d1117] font-semibold text-sm hover:bg-[#00bfa8] transition-colors"
            >
              View my tickets
            </Link>
            <button
              type="button"
              onClick={() => navigate('/app')}
              className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm hover:bg-gray-200 transition-colors"
            >
              Back to app
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-xl mx-auto">
        {/* Back link */}
        <a href={helpUrl('contact')} rel="noopener" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors mb-8">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
          </svg>
          Help Centre
        </a>

        <h1 className="text-2xl font-bold text-gray-900 mb-1">Log a Support Ticket</h1>
        <p className="text-gray-500 text-sm mb-8">
          Describe your issue and we'll get back to you within 1 business day.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">
              Subject <span className="text-[#007a6d]">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Brief description of your issue"
              maxLength={120}
              required
              className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#00d4b8] focus:ring-2 focus:ring-[#00d4b8]/40 transition-colors"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">
              Category <span className="text-[#007a6d]">*</span>
            </label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-[#00d4b8] focus:ring-2 focus:ring-[#00d4b8]/40 transition-colors appearance-none"
            >
              {CATEGORIES.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">
              Description <span className="text-[#007a6d]">*</span>
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe the issue in detail. Include steps to reproduce if it's a bug, or your transaction reference if it's a billing issue."
              rows={6}
              required
              className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#00d4b8] focus:ring-2 focus:ring-[#00d4b8]/40 transition-colors resize-none"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !subject.trim() || !description.trim()}
            className="w-full py-3 rounded-xl bg-[#00d4b8] text-[#0d1117] font-semibold text-sm hover:bg-[#00bfa8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Submitting…' : 'Submit ticket'}
          </button>
        </form>
      </div>
    </div>
  );
}
